/**
 * Het rechtermuismenu op een object.
 *
 * Staat apart omdat er meer dan één canvas is dat 'm nodig heeft — het gewone
 * React-Flow-canvas en het banen-canvas, waar zowel een kaart als de bewoner
 * van een baan een object is. Zonder deze scheiding had alleen het eerste
 * canvas een menu en kreeg je op het andere dat van de browser: precies het
 * soort verschil dat een gebruiker niet kan onthouden.
 *
 * De opdrachten zijn overal dezelfde en werken op het CONCEPT, niet op het
 * voorkomen: bewerken opent het attributenpaneel, tonen wijst 'm aan in de
 * Explorer, verwijderen haalt 'm uit het model (mét de gebruikelijke
 * bevestiging). Wat per canvas verschilt — samenvoegen, een ArchiMate-type
 * wisselen — komt als extra regel binnen via `extra`.
 */
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";

export interface ConceptMenuStand {
  x: number;
  y: number;
  /** Het concept waar het menu op werkt. */
  conceptId: string;
}

export function ConceptContextMenu({
  stand,
  workspaceId,
  branchId,
  onClose,
  extra,
}: {
  stand: ConceptMenuStand;
  workspaceId: string | null;
  branchId: string | null;
  onClose: () => void;
  /** Regels die alleen op dít canvas gelden. */
  extra?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  // Klikken buiten het menu of Escape sluit 'm — zoals elk menu in de app.
  useEffect(() => {
    const opKlik = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", opKlik);
    window.addEventListener("keydown", opToets);
    return () => {
      window.removeEventListener("mousedown", opKlik);
      window.removeEventListener("keydown", opToets);
    };
  }, [onClose]);

  const knop = "block w-full px-3 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)";

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: menu-oppervlak; de knoppen erin dragen de interactie
    <div
      ref={ref}
      className="ledger absolute z-20 min-w-[180px] rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 text-sm shadow-xl ring-1 ring-(--ld-border)"
      style={{ left: stand.x, top: stand.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={knop}
        onClick={() => {
          useUi.getState().setAttributePanelOpen(true);
          onClose();
        }}
      >
        {t("canvas.contextMenu.edit")}
      </button>
      <button
        type="button"
        className={knop}
        onClick={() => {
          onClose();
          const ui = useUi.getState();
          ui.setExplorerOpen(true);
          ui.revealInExplorer(stand.conceptId);
        }}
      >
        {t("canvas.contextMenu.showInExplorer")}
      </button>
      {extra}
      <button
        type="button"
        className="block w-full px-3 py-1 text-left text-(--ld-negative) hover:bg-(--ld-negative-bg)"
        onClick={() => {
          onClose();
          if (!workspaceId || !branchId) return;
          const ws = useWorkspaceData.getState();
          void (async () => {
            const langKey: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
            const ok = await confirmHardDelete({
              workspaceId,
              branchId,
              conceptIds: [stand.conceptId],
              lang: langKey,
              standaloneEdgeCount: 0,
              conceptName: (id) => ws.store.getConcept(id)?.name,
            });
            if (!ok) return;
            await ws.removeBatch(workspaceId, branchId, [stand.conceptId], []);
          })();
        }}
      >
        {t("canvas.contextMenu.delete")}
      </button>
    </div>
  );
}
